export type PricingFeatureStrike = {
    text: string;
    strike?: boolean;
};

import { SUBSCRIPTION_TRANSLATIONS } from "@/domains/subscription/constants";
import { TRIAL_DAYS } from "@/domains/subscription/subscriptionConstants";
import { getTranslation } from "@mivo/i18n";

export const SubscriptionPlanIds = {
    free: "Gratuito",
    basic: "Basico",
    pro: "Pro",
    clinic: "Clinic",
} as const;

export type SubscriptionPlanId = (typeof SubscriptionPlanIds)[keyof typeof SubscriptionPlanIds];

export type SubscriptionPlanDefinition = {
    id: SubscriptionPlanId;
    versionId?: string;
    name: string;
    patientLimit: number | null;
    storageLimitBytes: number;
    priceBrl?: number | null;
    priceLabel: string;
    periodLabel: string;
    benefits: (string | PricingFeatureStrike)[];
    description: string;
    cta: string;
    enabled: boolean;
    trialDays: number;
};

/** Canonical tier order for upgrade paths and checkout listing. */
export const PLAN_TIER_ORDER: readonly SubscriptionPlanId[] = [
    SubscriptionPlanIds.free,
    SubscriptionPlanIds.basic,
    SubscriptionPlanIds.pro,
    SubscriptionPlanIds.clinic,
];

const KNOWN_PLAN_SLUGS = new Set<string>(PLAN_TIER_ORDER);

/** Canonical pt-BR string returned by the backend when the plan patient limit is reached. */
export const PLAN_LIMIT_REACHED_DETAIL = SUBSCRIPTION_TRANSLATIONS.planLimitReachedDetail["pt-BR"];

export function getPlanLimitReachedMessage(): string {
    return getTranslation(SUBSCRIPTION_TRANSLATIONS.planLimitReachedDetail);
}

/** Maps API / legacy plan identifiers to catalog slugs. */
export function normalizePlanSlug(planId: string | null | undefined): SubscriptionPlanId {
    if (!planId) {
        return SubscriptionPlanIds.basic;
    }

    const trimmed = planId.trim();
    if (KNOWN_PLAN_SLUGS.has(trimmed)) {
        return trimmed as SubscriptionPlanId;
    }

    const lower = trimmed.toLowerCase();
    if (lower === "gratuito" || lower === "freemium" || lower === "free") {
        return SubscriptionPlanIds.free;
    }

    if (
        lower === "básico" ||
        lower === "basico" ||
        lower === "basic"
    ) {
        return SubscriptionPlanIds.basic;
    }

    if (
        lower === "pro" ||
        lower === "professional_30" ||
        lower === "proplus" ||
        lower === "pro+" ||
        lower === "professional_50"
    ) {
        return SubscriptionPlanIds.pro;
    }

    if (lower === "clinic" || lower === "clínica" || lower === "multi" || trimmed === "Clinic") {
        return SubscriptionPlanIds.clinic;
    }

    return SubscriptionPlanIds.basic;
}

export function isTrialPlan(planId: string | undefined | null): boolean {
    return normalizePlanSlug(planId) === SubscriptionPlanIds.basic;
}

export function isPaidPlan(planId: string | undefined | null): boolean {
    const slug = normalizePlanSlug(planId);
    return slug === SubscriptionPlanIds.pro || slug === SubscriptionPlanIds.clinic;
}

export function isFreePlan(planId: string | undefined | null): boolean {
    return normalizePlanSlug(planId) === SubscriptionPlanIds.free;
}

export function comparePlanTier(current: string | null | undefined, target: string | null | undefined): number {
    const currentIndex = PLAN_TIER_ORDER.indexOf(normalizePlanSlug(current));
    const targetIndex = PLAN_TIER_ORDER.indexOf(normalizePlanSlug(target));
    return targetIndex - currentIndex;
}

export function isUpgradePlan(current: string | null | undefined, target: string | null | undefined): boolean {
    return comparePlanTier(current, target) > 0;
}

export function isDowngradePlan(current: string | null | undefined, target: string | null | undefined): boolean {
    return comparePlanTier(current, target) < 0;
}

/** Multi (Clinic) → Básico/Pro keeps clinic tenancy (BR-MDR-10 / FR-MDR-02). */
export function isClinicKeepPlanDowngrade(
    current: string | null | undefined,
    target: string | null | undefined,
): boolean {
    const currentSlug = normalizePlanSlug(current);
    const targetSlug = normalizePlanSlug(target);
    if (currentSlug !== SubscriptionPlanIds.clinic) {
        return false;
    }

    return targetSlug === SubscriptionPlanIds.basic || targetSlug === SubscriptionPlanIds.pro;
}

function buildPlanStub(slug: SubscriptionPlanId): SubscriptionPlanDefinition {
    if (slug === SubscriptionPlanIds.free) {
        return {
            id: slug,
            name: getTranslation(SUBSCRIPTION_TRANSLATIONS.planNameFree),
            patientLimit: 10,
            storageLimitBytes: 0,
            priceLabel: getTranslation(SUBSCRIPTION_TRANSLATIONS.planPriceFree),
            periodLabel: "",
            benefits: [],
            description: "",
            cta: "",
            enabled: true,
            trialDays: 0,
        };
    }

    const name =
        slug === SubscriptionPlanIds.basic
            ? getTranslation(SUBSCRIPTION_TRANSLATIONS.planNameBasic)
            : slug === SubscriptionPlanIds.clinic
                ? getTranslation(SUBSCRIPTION_TRANSLATIONS.planNameClinic)
                : getTranslation(SUBSCRIPTION_TRANSLATIONS.planNamePro);
    const patientLimit =
        slug === SubscriptionPlanIds.basic ? 20 : slug === SubscriptionPlanIds.clinic ? 100 : slug === SubscriptionPlanIds.pro ? 100 : null;
    const storageLimitBytes =
        slug === SubscriptionPlanIds.basic ? 0 : 1_073_741_824;
    return {
        id: slug,
        name,
        patientLimit,
        storageLimitBytes,
        priceLabel: "",
        periodLabel: "",
        benefits: [],
        description: "",
        cta: "",
        enabled: true,
        trialDays: slug === SubscriptionPlanIds.basic || slug === SubscriptionPlanIds.pro ? TRIAL_DAYS : 0,
    };
}

export function orderCatalogByTier(
    catalog: readonly SubscriptionPlanDefinition[],
): SubscriptionPlanDefinition[] {
    const byId = new Map(catalog.map((plan) => [plan.id, plan]));
    const ordered: SubscriptionPlanDefinition[] = [];

    for (const id of PLAN_TIER_ORDER) {
        const plan = byId.get(id);
        if (plan) {
            ordered.push(plan);
        }
    }

    for (const plan of catalog) {
        if (!ordered.some((entry) => entry.id === plan.id)) {
            ordered.push(plan);
        }
    }

    return ordered;
}

export function findPlanInCatalog(
    catalog: readonly SubscriptionPlanDefinition[],
    planId: string | null | undefined,
): SubscriptionPlanDefinition | undefined {
    const slug = normalizePlanSlug(planId);
    return catalog.find((plan) => plan.id === slug);
}

export function getPlanFromCatalog(
    catalog: readonly SubscriptionPlanDefinition[],
    planId: string | null | undefined,
): SubscriptionPlanDefinition {
    return findPlanInCatalog(catalog, planId) ?? buildPlanStub(normalizePlanSlug(planId));
}

/** @deprecated Prefer `getPlanFromCatalog` with catalog from `usePlanCatalog`. */
export function getPlanById(
    planId: string | undefined | null,
    catalog?: readonly SubscriptionPlanDefinition[],
): SubscriptionPlanDefinition {
    return getPlanFromCatalog(catalog ?? [], planId);
}

export function nextPlan(
    planId: string | null | undefined,
    catalog: readonly SubscriptionPlanDefinition[],
): SubscriptionPlanDefinition | null {
    const slug = normalizePlanSlug(planId);
    const ordered = orderCatalogByTier(catalog);
    const index = ordered.findIndex((plan) => plan.id === slug);

    if (index < 0 || index >= ordered.length - 1) {
        return null;
    }

    return ordered[index + 1];
}

export function nextPlanBenefits(
    planId: string | null | undefined,
    catalog: readonly SubscriptionPlanDefinition[],
): string[] {
    const upcoming = nextPlan(planId, catalog);
    if (!upcoming) {
        return [];
    }

    return upcoming.benefits
        .filter((benefit) => typeof benefit === "string" || !benefit.strike)
        .map((benefit) => (typeof benefit === "string" ? benefit : benefit.text));
}

export function sellableCatalogPlans(
    catalog: readonly SubscriptionPlanDefinition[],
): SubscriptionPlanDefinition[] {
    return catalog.filter((plan) => plan.enabled);
}

export function selectableCheckoutPlans(
    catalog: readonly SubscriptionPlanDefinition[],
    isManageMode: boolean,
    paymentLocked: boolean,
    conversionCheckout = false,
): SubscriptionPlanDefinition[] {
    const sellable = sellableCatalogPlans(catalog);

    if (conversionCheckout) {
        return orderCatalogByTier(sellable).filter(
            (plan) => plan.id !== SubscriptionPlanIds.free && plan.id !== SubscriptionPlanIds.clinic,
        );
    }

    const paidPlans = sellable.filter(
        (plan) => plan.id !== SubscriptionPlanIds.basic && plan.id !== SubscriptionPlanIds.free,
    );
    let plans = isManageMode ? orderCatalogByTier(sellable) : paidPlans;

    if (paymentLocked) {
        plans = plans.filter(
            (plan) => plan.id !== SubscriptionPlanIds.basic && plan.id !== SubscriptionPlanIds.free,
        );
    }

    return plans;
}

export function formatStorageBytes(bytes: number): string {
    if (bytes <= 0) {
        return "0 B";
    }

    const units = ["B", "KB", "MB", "GB"] as const;
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }

    const formatted =
        unitIndex >= 2
            ? Math.round(value).toString()
            : Math.abs(value - Math.round(value)) < 0.05
                ? Math.round(value).toString()
                : value.toFixed(1);
    return `${formatted} ${units[unitIndex]}`;
}

export function formatPatientLimit(limit: number | null | undefined): string {
    return limit == null ? getTranslation(SUBSCRIPTION_TRANSLATIONS.unlimitedPatients) : String(limit);
}

/** Price line from catalog/API only — returns null when plan has no publishable price. */
export function formatCatalogPlanPrice(plan: SubscriptionPlanDefinition): string | null {
    if (plan.priceLabel.trim() && plan.periodLabel.trim()) {
        return `${plan.priceLabel.trim()}${plan.periodLabel.trim()}`;
    }

    if (plan.priceBrl != null && Number.isFinite(plan.priceBrl)) {
        const amount = plan.priceBrl.toFixed(2).replace(".", ",");
        const period = plan.periodLabel.trim() || getTranslation(SUBSCRIPTION_TRANSLATIONS.perMonthPeriod);
        return `R$ ${amount}${period}`;
    }

    return null;
}
